import { FC } from 'react';

import { getBannerIdForCopy } from 'utils/campaign';
import { isAccountStatusReadOnly } from 'utils/clickme';
import { CampaignArchiveReason, CampaignStatus } from 'utils/constants/dashboard';

import dashboardCampaignButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_campaign_button_click';
import { Link } from '@hh.ru/redux-spa-middleware';

import Button, { ButtonAppearance, ButtonIconPosition } from 'bloko/blocks/button';
import Tip, { TipPlacement, TipTheme } from 'bloko/blocks/drop/Tip';
import HoverTip from 'bloko/blocks/drop/Tip/HoverTip';
import HSpacing from 'bloko/blocks/hSpacing';
import Icon, { IconScale, IconView } from 'bloko/blocks/icon';
import VSpacing from 'bloko/blocks/vSpacing';

import useTrls from 'hooks/translation/useTrls';

import { Banner, FrontendStatusType } from 'store/models/dashboard';
import { useSelector } from 'store/utils';

interface SettingsProps {
    campaignId: number;
    maxBannersInCampaign: number;
    countBanners: number;
    banners: Banner[];
    frontendStatus: FrontendStatusType;
    archiveReason: CampaignArchiveReason | null;
    isAuction?: boolean;
}

const isArchiveReasonTransferOrExpiredAccount = (archiveReason: CampaignArchiveReason | null): boolean =>
    !!(archiveReason && [CampaignArchiveReason.ExpiredAccount, CampaignArchiveReason.Transfer].includes(archiveReason));

const Settings: FC<SettingsProps> = ({
    campaignId,
    maxBannersInCampaign,
    countBanners,
    banners,
    frontendStatus,
    archiveReason,
    isAuction = false,
}) => {
    const currentAccountStatus = useSelector((state) => state.user.user?.currentAccount.status);

    const trl = useTrls();

    const onClickAddBanner = () => {
        dashboardCampaignButtonClick({ buttonName: 'campaign_add_banner', campaignId });
    };
    const onClickSettingsEdit = () => {
        dashboardCampaignButtonClick({ buttonName: 'campaign_edit', campaignId });
    };
    const onClickCopy = () => {
        dashboardCampaignButtonClick({ buttonName: 'campaign_copy', campaignId });
    };

    const isArchivedCampaign = frontendStatus === CampaignStatus.Archived;

    if (
        (isArchivedCampaign && isArchiveReasonTransferOrExpiredAccount(archiveReason)) ||
        isAccountStatusReadOnly(currentAccountStatus)
    ) {
        return null;
    }

    return (
        <>
            {maxBannersInCampaign > countBanners && !isAuction && !isArchivedCampaign && (
                <>
                    <Button
                        onClick={onClickAddBanner}
                        appearance={ButtonAppearance.Outlined}
                        icon={<Icon view={IconView.Plus} scale={IconScale.Small} />}
                        iconPosition={ButtonIconPosition.Left}
                        Element={Link}
                        to={`/new/campaign/${campaignId}/banner/`}
                        data-qa="ad-block__banner-add"
                    >
                        {trl('campaign.settings.addBanner')}
                    </Button>
                    <HSpacing base={1} />
                </>
            )}
            {!isArchivedCampaign && (
                <Button
                    onClick={onClickSettingsEdit}
                    appearance={ButtonAppearance.Outlined}
                    icon={<Icon view={IconView.Gear} scale={IconScale.Small} />}
                    iconPosition={ButtonIconPosition.Left}
                    Element={Link}
                    target="_blank"
                    to={`/new/campaign/${campaignId}/`}
                    data-qa="campaign-edit-button"
                >
                    {trl('campaign.settings.edit')}
                </Button>
            )}
            {banners.length > 0 &&
                !isAuction &&
                !(isArchivedCampaign && isArchiveReasonTransferOrExpiredAccount(archiveReason)) && (
                    <>
                        <HSpacing base={1} />
                        <HoverTip
                            placement={TipPlacement.Right}
                            theme={TipTheme.Dark}
                            DropElement={Tip}
                            host={document.body}
                            render={() => trl('campaign.settings.copyBannerTooltip')}
                        >
                            <Button
                                onClick={onClickCopy}
                                appearance={ButtonAppearance.Outlined}
                                icon={<Icon view={IconView.Squares} scale={IconScale.Small} />}
                                iconPosition={ButtonIconPosition.Left}
                                Element={Link}
                                to={`/new/campaign/${campaignId}/banner/${getBannerIdForCopy(banners)}/copy`}
                                data-qa="campaign-copy-button"
                            >
                                {trl('campaign.settings.copyBanner')}
                            </Button>
                        </HoverTip>
                    </>
                )}
            <VSpacing base={4} />
        </>
    );
};

export default Settings;
